import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

const CharLimitsField = ({ value, onSave, disabled }) => {
  const [draft, setDraft] = useState(JSON.stringify(value || {}, null, 2));
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    setDraft(JSON.stringify(value || {}, null, 2));
  }, [value]);

  const handleSave = () => {
    try {
      const parsed = JSON.parse(draft);
      setJsonError('');
      onSave(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  return (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Platform Character Limits (JSON)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setJsonError('');
            }}
            disabled={disabled}
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            isInvalid={!!jsonError}
          />
          {jsonError && <Form.Control.Feedback type="invalid">{jsonError}</Form.Control.Feedback>}
          <Form.Text className="text-muted">
            Character limits per platform for alternative headlines.
          </Form.Text>
          <div className="mt-2">
            <Button size="sm" variant="outline-primary" onClick={handleSave} disabled={disabled}>
              Save Limits
            </Button>
          </div>
        </Form.Group>
      </Col>
    </Row>
  );
};

/**
 * Tool Settings Component
 * Configure audio quality, image size, and model selection
 */
const ToolSettings = () => {
  const [settings, setSettings] = useState({});
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    setUsageLoading(true);
    try {
      const response = await fetch('/api/admin/tools/model-usage', { credentials: 'include' });
      const data = await response.json();
      if (data.windows) {
        setUsageData(data);
      }
    } catch (err) {
      console.error('Failed to fetch model usage data:', err);
    }
    setUsageLoading(false);
  };

  const fetchData = async () => {
    try {
      const [settingsRes, modelsRes] = await Promise.all([
        fetch('/api/admin/tools/settings', { credentials: 'include' }),
        fetch('/api/admin/tools/models', { credentials: 'include' })
      ]);

      const settingsData = await settingsRes.json();
      const modelsData = await modelsRes.json();

      // Validate that saved models still exist in active models
      const activeModels = modelsData.models.filter(m => m.is_active);
      const newWarnings = [];
      const settingsToUpdate = {};

      // Check text model - validate by model_ref_id or legacy model string
      const textSetting = settingsData.settings.openai_text_model;
      const textModelsActive = activeModels.filter(m => m.model_type === 'text');
      const textModelValid = textSetting?.model_ref_id
        ? textModelsActive.find(m => m.id === textSetting.model_ref_id)
        : textSetting?.model
          ? textModelsActive.find(m => m.model_id === textSetting.model)
          : null;

      if (textSetting && !textModelValid) {
        const savedName = textSetting.model || `ID ${textSetting.model_ref_id}`;
        const recommended = textModelsActive.find(m => m.is_recommended) || textModelsActive[0];
        if (recommended) {
          newWarnings.push(`Text model "${savedName}" no longer exists. Auto-selecting "${recommended.model_name}".`);
          settingsToUpdate.openai_text_model = { model_ref_id: recommended.id };
        }
      }

      // Check TTS model
      const ttsSetting = settingsData.settings.openai_tts_model;
      const ttsModelsActive = activeModels.filter(m => m.model_type === 'tts');
      const ttsModelValid = ttsSetting?.model_ref_id
        ? ttsModelsActive.find(m => m.id === ttsSetting.model_ref_id)
        : ttsSetting?.model
          ? ttsModelsActive.find(m => m.model_id === ttsSetting.model)
          : null;

      if (ttsSetting && !ttsModelValid) {
        const savedName = ttsSetting.model || `ID ${ttsSetting.model_ref_id}`;
        const recommended = ttsModelsActive.find(m => m.is_recommended) || ttsModelsActive[0];
        if (recommended) {
          newWarnings.push(`TTS model "${savedName}" no longer exists. Auto-selecting "${recommended.model_name}".`);
          settingsToUpdate.openai_tts_model = { model_ref_id: recommended.id };
        }
      }

      // Check image model
      const imageSetting = settingsData.settings.openai_image_model;
      const imageModelsActive = activeModels.filter(m => m.model_type === 'image');
      const imageModelValid = imageSetting?.model_ref_id
        ? imageModelsActive.find(m => m.id === imageSetting.model_ref_id)
        : imageSetting?.model
          ? imageModelsActive.find(m => m.model_id === imageSetting.model)
          : null;

      if (imageSetting && !imageModelValid) {
        const savedName = imageSetting.model || `ID ${imageSetting.model_ref_id}`;
        const recommended = imageModelsActive.find(m => m.is_recommended) || imageModelsActive[0];
        if (recommended) {
          newWarnings.push(`Image model "${savedName}" no longer exists. Auto-selecting "${recommended.model_name}".`);
          settingsToUpdate.openai_image_model = { model_ref_id: recommended.id };
        }
      }

      setWarnings(newWarnings);
      setSettings({ ...settingsData.settings, ...settingsToUpdate });
      setModels(modelsData.models);
      setLoading(false);

      // Persist any auto-corrected settings
      for (const [key, value] of Object.entries(settingsToUpdate)) {
        try {
          await fetch(`/api/admin/tools/settings/${key}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ value })
          });
        } catch (err) {
          console.error(`Failed to auto-update ${key}:`, err);
        }
      }
    } catch (err) {
      setError('Failed to load settings');
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/admin/tools/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value })
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        // For model settings, enrich the local state with resolved model details
        let enrichedValue = value;
        const modelSettingKeys = ['openai_text_model', 'openai_tts_model', 'openai_image_model'];
        if (modelSettingKeys.includes(key) && value.model_ref_id) {
          const model = models.find(m => m.id === value.model_ref_id);
          if (model) {
            enrichedValue = {
              model_ref_id: value.model_ref_id,
              model: model.model_id,
              ...(model.model_type === 'text' && {
                input_cost_per_million: model.input_cost_per_million,
                output_cost_per_million: model.output_cost_per_million
              }),
              ...(model.model_type === 'tts' && {
                cost_per_minute: model.cost_per_minute,
                cost_per_million_chars: model.cost_per_million_chars
              }),
              ...(model.model_type === 'image' && {
                quality: model.image_quality,
                cost_per_image: model.cost_per_image
              })
            };
          }
        }
        setSettings({ ...settings, [key]: enrichedValue });
        setSuccess('Setting updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to update setting');
    }

    setSaving(false);
  };

  const audioFormatOptions = [
    { value: 'mp3', label: 'MP3 (most compatible)' },
    { value: 'opus', label: 'Opus (smaller file size)' },
    { value: 'aac', label: 'AAC (Apple devices)' },
    { value: 'flac', label: 'FLAC (lossless)' }
  ];

  const imageSizeOptions = [
    { value: 'landscape', label: 'Landscape (Facebook/Twitter)', width: 1200, height: 630 },
    { value: 'square', label: 'Square (Instagram)', width: 1024, height: 1024 },
    { value: 'portrait', label: 'Portrait (Stories/Reels)', width: 630, height: 1200 }
  ];

  // Model-specific size support
  // DALL-E 2 only supports square up to 1024x1024
  // DALL-E 3 and gpt-image-1 family support all three sizes
  const getModelSizeSupport = (modelId) => {
    if (!modelId) return ['landscape', 'square', 'portrait'];
    const id = modelId.toLowerCase();
    if (id.includes('dall-e-2') || id === 'dalle-2') {
      return ['square']; // DALL-E 2 only supports 1024x1024 max
    }
    // DALL-E 3, gpt-image-1, gpt-image-1-mini all support all sizes
    return ['landscape', 'square', 'portrait'];
  };

  const currentImageModel = settings.openai_image_model?.model || 'gpt-image-1-mini';
  const supportedSizes = getModelSizeSupport(currentImageModel);

  // Map provider abbreviations to display labels
  const getProviderLabel = (provider) => {
    if (!provider) return 'Unknown';
    const providerMap = {
      'openai': 'OpenAI',
      'anthropic': 'Anthropic',
      'openrouter': 'OpenRouter'
    };
    return providerMap[provider.toLowerCase()] || provider;
  };

  // Build a map of usage data keyed by provider|model_id for quick lookup
  const getUsageMap = () => {
    const map = new Map();
    if (!usageData) return map;
    ['1d', '7d', '30d'].forEach(window => {
      usageData.windows[window].forEach(row => {
        const key = `${row.provider}|${row.modelUsed}`;
        if (!map.has(key)) {
          map.set(key, {});
        }
        map.get(key)[window] = row;
      });
    });
    return map;
  };

  // Format combined in/out values: "12,340 / 5,670"
  const formatTokens = (inTokens, outTokens) => {
    if (!inTokens && !outTokens) return '-';
    return `${(inTokens || 0).toLocaleString()} / ${(outTokens || 0).toLocaleString()}`;
  };

  // Format price as combined in/out: "$3.00 / $15.00"
  const formatPrice = (inPrice, outPrice) => {
    if (!inPrice && !outPrice) return '-';
    return `$${inPrice || 0} / $${outPrice || 0}`;
  };

  // Get usage for a model in a specific window
  const getUsageForWindow = (usageMap, provider, modelId, window) => {
    const key = `${provider}|${modelId}`;
    const data = usageMap.get(key)?.[window];
    if (!data) return null;
    return { inputTokens: data.inputTokens, outputTokens: data.outputTokens };
  };

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  const textModels = models.filter(m => m.model_type === 'text' && m.is_active);
  const ttsModels = models.filter(m => m.model_type === 'tts' && m.is_active);
  const imageModels = models.filter(m => m.model_type === 'image' && m.is_active);
  const videoModels = models.filter(m => m.model_type === 'video' && m.is_active);

  // Group models by provider
  const groupModelsByProvider = (modelList) => {
    const providers = [...new Set(modelList.map(m => m.provider))].sort();
    return providers.map(provider => ({
      provider,
      models: modelList.filter(m => m.provider === provider)
    }));
  };

  return (
    <AdminToolsLayout>
    <Card>
      <Card.Header>
        <h5 className="mb-0">Tool Settings</h5>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {warnings.length > 0 && (
          <Alert variant="warning" dismissible onClose={() => setWarnings([])}>
            <Alert.Heading>Model Configuration Updated</Alert.Heading>
            <ul className="mb-0">
              {warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
            <hr />
            <p className="mb-0 small">
              This can happen when models are renamed or deactivated in the Admin Console.
              The settings have been auto-corrected to use available models.
            </p>
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <h6 className="mb-3">Audio Settings</h6>

            <Form.Group className="mb-3">
              <Form.Label>Audio Format</Form.Label>
              <Form.Select
                value={settings.article_tool_audio_format?.format || 'mp3'}
                onChange={(e) => {
                  const selected = audioFormatOptions.find(o => o.value === e.target.value);
                  updateSetting('article_tool_audio_format', {
                    format: selected.value
                  });
                }}
                disabled={saving}
              >
                {audioFormatOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Audio Sample Duration (seconds)</Form.Label>
              <Form.Control
                type="number"
                value={settings.article_tool_audio_sample_seconds?.seconds || 30}
                onChange={(e) => updateSetting('article_tool_audio_sample_seconds', { seconds: parseInt(e.target.value) })}
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Free tool always uses AI Summary mode. Tenants can override mode and duration in their settings.
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <h6 className="mb-3">Image Settings</h6>

            <Form.Group className="mb-3">
              <Form.Label>Image Size Preset</Form.Label>
              <Form.Select
                value={settings.article_tool_image_size?.preset || 'landscape'}
                onChange={(e) => {
                  const selected = imageSizeOptions.find(o => o.value === e.target.value);
                  updateSetting('article_tool_image_size', {
                    preset: selected.value,
                    width: selected.width,
                    height: selected.height
                  });
                }}
                disabled={saving}
              >
                {imageSizeOptions.map(opt => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    disabled={!supportedSizes.includes(opt.value)}
                  >
                    {opt.label} {!supportedSizes.includes(opt.value) && '(not supported by selected model)'}
                  </option>
                ))}
              </Form.Select>
              {supportedSizes.length === 1 && (
                <Form.Text className="text-muted">
                  DALL-E 2 only supports square images (1024x1024)
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Image Style Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={settings.image_generation_style?.text || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  image_generation_style: { text: e.target.value }
                })}
                onBlur={(e) => updateSetting('image_generation_style', { text: e.target.value })}
                disabled={saving}
                style={{ fontSize: '0.85rem' }}
              />
              <Form.Text className="text-muted">
                These instructions control the visual style of generated images. They are injected
                into the image prompt via the {'{{style}}'} variable.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <hr className="my-4" />

        <Row>
          <Col md={6}>
            <h6 className="mb-3">ScrapeDo Configuration</h6>

            <Form.Group className="mb-3">
              <Form.Label>Cost per Credit ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                value={settings.scrapedo_cost_per_credit?.cost || 0.0001}
                onChange={(e) => updateSetting('scrapedo_cost_per_credit', { cost: parseFloat(e.target.value) })}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Simple Fetch Credits</Form.Label>
              <Form.Control
                type="number"
                value={settings.scrapedo_simple_credits?.credits || 1}
                onChange={(e) => updateSetting('scrapedo_simple_credits', { credits: parseInt(e.target.value) })}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>JavaScript Render Credits</Form.Label>
              <Form.Control
                type="number"
                value={settings.scrapedo_js_credits?.credits || 1}
                onChange={(e) => updateSetting('scrapedo_js_credits', { credits: parseInt(e.target.value) })}
                disabled={saving}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <h6 className="mb-3">Model Selection</h6>

            <Form.Group className="mb-3">
              <Form.Label>Text Model</Form.Label>
              <Form.Select
                value={settings.openai_text_model?.model_ref_id || textModels.find(m => m.model_id === settings.openai_text_model?.model)?.id || ''}
                onChange={(e) => {
                  const model = textModels.find(m => m.id === parseInt(e.target.value));
                  if (model) {
                    updateSetting('openai_text_model', {
                      model_ref_id: model.id
                    });
                  }
                }}
                disabled={saving}
              >
                {groupModelsByProvider(textModels).map(group => (
                  <optgroup key={group.provider} label={group.provider}>
                    {group.models.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.model_name} {m.is_recommended && '(Recommended)'}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Select>
              {(() => {
                const selectedTextModel = textModels.find(m => m.id === (settings.openai_text_model?.model_ref_id || textModels.find(m2 => m2.model_id === settings.openai_text_model?.model)?.id));
                return (
                  <>
                    {selectedTextModel?.notes && (
                      <Form.Text className="text-muted d-block mt-1">
                        <em>{selectedTextModel.notes}</em>
                      </Form.Text>
                    )}
                    {selectedTextModel && selectedTextModel.supports_vision === false && (
                      <Form.Text className="text-warning d-block mt-1">
                        <strong>Warning:</strong> This model does not support vision/image inputs. Image alt text generation requires a vision-capable model (e.g., gpt-4o, gpt-4o-mini).
                      </Form.Text>
                    )}
                  </>
                );
              })()}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>TTS Model</Form.Label>
              <Form.Select
                value={settings.openai_tts_model?.model_ref_id || ttsModels.find(m => m.model_id === settings.openai_tts_model?.model)?.id || ''}
                onChange={(e) => {
                  const model = ttsModels.find(m => m.id === parseInt(e.target.value));
                  if (model) {
                    updateSetting('openai_tts_model', {
                      model_ref_id: model.id
                    });
                  }
                }}
                disabled={saving}
              >
                {groupModelsByProvider(ttsModels).map(group => (
                  <optgroup key={group.provider} label={group.provider}>
                    {group.models.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.model_name} {m.is_recommended && '(Recommended)'}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Select>
              {(() => {
                const selectedTtsModel = ttsModels.find(m => m.id === (settings.openai_tts_model?.model_ref_id || ttsModels.find(m2 => m2.model_id === settings.openai_tts_model?.model)?.id));
                return selectedTtsModel?.notes && (
                  <Form.Text className="text-muted d-block mt-1">
                    <em>{selectedTtsModel.notes}</em>
                  </Form.Text>
                );
              })()}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Voice Override</Form.Label>
              <Form.Select
                value={settings.article_tool_tts_voice?.voice || ''}
                onChange={(e) => {
                  updateSetting('article_tool_tts_voice', {
                    voice: e.target.value || null
                  });
                }}
                disabled={saving}
              >
                <option value="">Use model default</option>
                <option value="alloy">Alloy (neutral, balanced)</option>
                <option value="echo">Echo (warm, conversational)</option>
                <option value="fable">Fable (expressive, British)</option>
                <option value="onyx">Onyx (deep, authoritative)</option>
                <option value="nova">Nova (friendly)</option>
                <option value="shimmer">Shimmer (clear, upbeat)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Override the model's default voice for this tool
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Image Model</Form.Label>
              <Form.Select
                value={settings.openai_image_model?.model_ref_id || imageModels.find(m => m.model_id === settings.openai_image_model?.model)?.id || ''}
                onChange={async (e) => {
                  const model = imageModels.find(m => m.id === parseInt(e.target.value));
                  if (model) {
                    await updateSetting('openai_image_model', {
                      model_ref_id: model.id
                    });

                    // Check if current size is supported by new model
                    const newSupportedSizes = getModelSizeSupport(model.model_id);
                    const currentSize = settings.article_tool_image_size?.preset || 'landscape';
                    if (!newSupportedSizes.includes(currentSize)) {
                      // Reset to first supported size
                      const newSize = imageSizeOptions.find(o => newSupportedSizes.includes(o.value));
                      if (newSize) {
                        updateSetting('article_tool_image_size', {
                          preset: newSize.value,
                          width: newSize.width,
                          height: newSize.height
                        });
                      }
                    }
                  }
                }}
                disabled={saving}
              >
                {groupModelsByProvider(imageModels).map(group => (
                  <optgroup key={group.provider} label={group.provider}>
                    {group.models.map(m => {
                      let priceLabel = '';
                      if (m.cost_per_image) {
                        priceLabel = ` - $${m.cost_per_image}/image`;
                      } else if (m.input_cost_per_million || m.output_cost_per_million) {
                        priceLabel = ` - $${m.input_cost_per_million || 0}/$${m.output_cost_per_million || 0} per 1M`;
                      }
                      return (
                        <option key={m.id} value={m.id}>
                          {m.model_name}{priceLabel} {m.is_recommended && '(Recommended)'}
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </Form.Select>
              {(() => {
                const selectedImageModel = imageModels.find(m => m.id === (settings.openai_image_model?.model_ref_id || imageModels.find(m2 => m2.model_id === settings.openai_image_model?.model)?.id));
                return selectedImageModel?.notes && (
                  <Form.Text className="text-muted d-block mt-1">
                    <em>{selectedImageModel.notes}</em>
                  </Form.Text>
                );
              })()}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Video Model</Form.Label>
              <Form.Select
                value={settings.article_tool_video_model?.model_ref_id || videoModels.find(m => m.model_id === settings.article_tool_video_model?.model)?.id || ''}
                onChange={(e) => {
                  const model = videoModels.find(m => m.id === parseInt(e.target.value));
                  if (model) {
                    updateSetting('article_tool_video_model', {
                      model_ref_id: model.id
                    });
                  }
                }}
                disabled={saving || videoModels.length === 0}
              >
                {videoModels.length === 0 ? (
                  <option disabled>No video models configured</option>
                ) : (
                  groupModelsByProvider(videoModels).map(group => (
                    <optgroup key={group.provider} label={group.provider}>
                      {group.models.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.model_name} {m.is_recommended && '(Recommended)'}
                        </option>
                      ))}
                    </optgroup>
                  ))
                )}
              </Form.Select>
              {videoModels.length === 0 && (
                <Form.Text className="text-muted">
                  No video models configured (groundwork; video generation is a follow-on)
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>

        <hr className="my-4" />

        <h6 className="mb-3">Alternative Headlines</h6>
        <CharLimitsField
          value={settings.alt_headline_char_limits}
          onSave={(parsed) => updateSetting('alt_headline_char_limits', parsed)}
          disabled={saving}
        />

        <hr className="my-4" />

        <h6 className="mb-3">Margin Guardrail Thresholds</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Warn Threshold (Markup)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={settings.margin_thresholds?.warn || 2.00}
                onChange={(e) => updateSetting('margin_thresholds', {
                  warn: parseFloat(e.target.value),
                  critical: settings.margin_thresholds?.critical || 1.00
                })}
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Markup fraction. 2.00 = 200% target, 1.00 = 100% floor (cost).
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Critical Threshold (Markup)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={settings.margin_thresholds?.critical || 1.00}
                onChange={(e) => updateSetting('margin_thresholds', {
                  warn: settings.margin_thresholds?.warn || 2.00,
                  critical: parseFloat(e.target.value)
                })}
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Markup fraction. 2.00 = 200% target, 1.00 = 100% floor (cost).
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Form.Text className="text-muted d-block mb-3">
              Used by the Finances view and margin alerts; billing never serves a model at or below cost.
            </Form.Text>
          </Col>
        </Row>

        <hr className="my-4" />

        <h6 className="mb-3">Model Costs Reference</h6>
        <p className="text-muted small mb-3">
          Current pricing and usage for available models.
        </p>

        {usageLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <p className="text-muted mt-2 small">Loading usage data...</p>
          </div>
        )}

        {models.length > 0 && (
          <>
            {(() => {
              const usageMap = getUsageMap();

              return (
                <>
                  <Card className="mb-3">
                    <Card.Header className="py-2">
                      <small className="fw-bold">Text Models</small>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th>Source</th>
                            <th>Model</th>
                            <th className="text-end">Price /1M</th>
                            <th className="text-end">1d Usage</th>
                            <th className="text-end">7d Usage</th>
                            <th className="text-end">30d Usage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {models.filter(m => m.model_type === 'text' && m.is_active).map(m => {
                            const usage1d = getUsageForWindow(usageMap, m.provider, m.model_id, '1d');
                            const usage7d = getUsageForWindow(usageMap, m.provider, m.model_id, '7d');
                            const usage30d = getUsageForWindow(usageMap, m.provider, m.model_id, '30d');
                            return (
                              <tr key={m.id} style={{ backgroundColor: m.is_recommended ? '#e8f5e9' : 'inherit' }}>
                                <td>{getProviderLabel(m.provider)}</td>
                                <td>{m.model_name} {m.is_recommended && '★'}</td>
                                <td className="text-end">{formatPrice(m.input_cost_per_million, m.output_cost_per_million)}</td>
                                <td className="text-end">{usage1d ? formatTokens(usage1d.inputTokens, usage1d.outputTokens) : '-'}</td>
                                <td className="text-end">{usage7d ? formatTokens(usage7d.inputTokens, usage7d.outputTokens) : '-'}</td>
                                <td className="text-end">{usage30d ? formatTokens(usage30d.inputTokens, usage30d.outputTokens) : '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Header className="py-2">
                      <small className="fw-bold">TTS Models</small>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th>Source</th>
                            <th>Model</th>
                            <th className="text-end">Price</th>
                            <th className="text-end">1d Usage</th>
                            <th className="text-end">7d Usage</th>
                            <th className="text-end">30d Usage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {models.filter(m => m.model_type === 'tts' && m.is_active).map(m => {
                            const usage1d = getUsageForWindow(usageMap, m.provider, m.model_id, '1d');
                            const usage7d = getUsageForWindow(usageMap, m.provider, m.model_id, '7d');
                            const usage30d = getUsageForWindow(usageMap, m.provider, m.model_id, '30d');
                            let priceLabel = '';
                            if (m.cost_per_minute) {
                              priceLabel = `$${m.cost_per_minute}/min`;
                            } else if (m.cost_per_million_chars) {
                              priceLabel = `$${m.cost_per_million_chars}/1M`;
                            } else {
                              priceLabel = '-';
                            }
                            return (
                              <tr key={m.id} style={{ backgroundColor: m.is_recommended ? '#e8f5e9' : 'inherit' }}>
                                <td>{getProviderLabel(m.provider)}</td>
                                <td>{m.model_name} {m.is_recommended && '★'}</td>
                                <td className="text-end">{priceLabel}</td>
                                <td className="text-end">{usage1d ? formatTokens(usage1d.inputTokens, usage1d.outputTokens) : '-'}</td>
                                <td className="text-end">{usage7d ? formatTokens(usage7d.inputTokens, usage7d.outputTokens) : '-'}</td>
                                <td className="text-end">{usage30d ? formatTokens(usage30d.inputTokens, usage30d.outputTokens) : '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Header className="py-2">
                      <small className="fw-bold">Image Models</small>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                        <thead>
                          <tr>
                            <th>Source</th>
                            <th>Model</th>
                            <th className="text-end">Price</th>
                            <th className="text-end">1d Usage</th>
                            <th className="text-end">7d Usage</th>
                            <th className="text-end">30d Usage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {models.filter(m => m.model_type === 'image' && m.is_active).map(m => {
                            const usage1d = getUsageForWindow(usageMap, m.provider, m.model_id, '1d');
                            const usage7d = getUsageForWindow(usageMap, m.provider, m.model_id, '7d');
                            const usage30d = getUsageForWindow(usageMap, m.provider, m.model_id, '30d');
                            let priceLabel = '';
                            if (m.cost_per_image) {
                              priceLabel = `$${m.cost_per_image}/img`;
                            } else if (m.input_cost_per_million || m.output_cost_per_million) {
                              priceLabel = formatPrice(m.input_cost_per_million, m.output_cost_per_million);
                            } else {
                              priceLabel = '-';
                            }
                            return (
                              <tr key={m.id} style={{ backgroundColor: m.is_recommended ? '#e8f5e9' : 'inherit' }}>
                                <td>{getProviderLabel(m.provider)}</td>
                                <td>{m.model_name} {m.is_recommended && '★'}</td>
                                <td className="text-end">{priceLabel}</td>
                                <td className="text-end">{usage1d ? formatTokens(usage1d.inputTokens, usage1d.outputTokens) : '-'}</td>
                                <td className="text-end">{usage7d ? formatTokens(usage7d.inputTokens, usage7d.outputTokens) : '-'}</td>
                                <td className="text-end">{usage30d ? formatTokens(usage30d.inputTokens, usage30d.outputTokens) : '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Card.Body>
                  </Card>
                </>
              );
            })()}
          </>
        )}

        <div className="text-muted small mb-3">
          ★ = Recommended model. Highlighted rows indicate recommended choices. Usage shows input / output tokens.
        </div>
      </Card.Body>
    </Card>
    </AdminToolsLayout>
  );
};

export default ToolSettings;
